import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  FileText,
  ChevronLeft,
  ChevronRight,
  Shuffle,
  Zap,
  Sliders,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@azyntor-labs/nexus-ui';
import { questionBanksApi, questionsApi, examsApi } from '@/api/endpoints';
import { Question, QuestionBank } from '@/types';
import { toast } from '@/utils/toast';

export const AddQuestionsFromBank = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL params
  const initialBankId = searchParams.get('bankId') || '';
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [selectedBankId, setSelectedBankId] = useState<string>(initialBankId);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [questionsToAdd, setQuestionsToAdd] = useState<Set<string>>(new Set());
  const [showSmartSelection, setShowSmartSelection] = useState(false);
  const [randomCount, setRandomCount] = useState<number>(10);
  const [customMix, setCustomMix] = useState({
    easy: 0,
    medium: 0,
    hard: 0,
  });
  const perPage = 20;

  // Fetch exam questions to know which are already added
  const { data: examQuestionsResponse } = useQuery({
    queryKey: ['exam-questions', examId],
    queryFn: () => examsApi.getQuestions(examId!),
    enabled: !!examId,
  });

  const existingQuestionIds = new Set(
    (examQuestionsResponse?.data?.data || []).map((q: Question) => q.id)
  );

  // Fetch question banks
  const { data: banksResponse } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => questionBanksApi.list({ page: 1, per_page: 100 }),
  });

  const questionBanks = Array.isArray(banksResponse?.data?.data?.data)
    ? banksResponse.data.data.data
    : [];

  // Fetch questions for selected bank with pagination
  const { data: questionsResponse, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ['questions', selectedBankId, currentPage, searchQuery],
    queryFn: () =>
      questionsApi.list({
        question_bank_id: selectedBankId,
        page: currentPage,
        per_page: perPage,
        search: searchQuery || undefined,
      }),
    enabled: !!selectedBankId,
  });

  // Fetch ALL questions for smart selection (no pagination)
  const { data: allQuestionsResponse, isLoading: isLoadingAllQuestions } = useQuery({
    queryKey: ['all-questions', selectedBankId],
    queryFn: () => questionsApi.getAllFromBank(selectedBankId),
    enabled: !!selectedBankId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const questions = Array.isArray(questionsResponse?.data?.data?.data)
    ? questionsResponse.data.data.data
    : [];
  // Fix: Axios response.data contains our API response which has data.questions
  const allQuestions = Array.isArray(allQuestionsResponse?.data?.data?.questions)
    ? allQuestionsResponse.data.data.questions
    : [];
  const totalQuestions = questionsResponse?.data?.data?.total || 0;
  const totalPages = Math.ceil(totalQuestions / perPage);

  // Update URL params when bank or page changes
  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedBankId) params.bankId = selectedBankId;
    if (currentPage > 1) params.page = currentPage.toString();
    setSearchParams(params);
  }, [selectedBankId, currentPage, setSearchParams]);

  // Reset to page 1 when bank or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBankId, searchQuery]);

  const handleBankChange = (bankId: string) => {
    setSelectedBankId(bankId);
    setQuestionsToAdd(new Set());
  };

  const toggleQuestionSelection = (questionId: string) => {
    const newSet = new Set(questionsToAdd);
    if (newSet.has(questionId)) {
      newSet.delete(questionId);
    } else {
      newSet.add(questionId);
    }
    setQuestionsToAdd(newSet);
  };

  const selectAllOnPage = () => {
    const newSet = new Set(questionsToAdd);
    questions
      .filter((q: Question) => !existingQuestionIds.has(q.id))
      .forEach((q: Question) => newSet.add(q.id));
    setQuestionsToAdd(newSet);
  };

  const deselectAllOnPage = () => {
    const newSet = new Set(questionsToAdd);
    questions.forEach((q: Question) => newSet.delete(q.id));
    setQuestionsToAdd(newSet);
  };

  const selectAllInBank = () => {
    const newSet = new Set<string>();
    allQuestions
      .filter((q: Question) => !existingQuestionIds.has(q.id))
      .forEach((q: Question) => newSet.add(q.id));
    setQuestionsToAdd(newSet);
    toast.success(`${newSet.size} questions selected from entire bank`);
  };

  const selectRandom = () => {
    const available = allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id));
    if (available.length === 0) {
      toast.error('No questions available');
      return;
    }

    const count = Math.min(randomCount, available.length);
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, count);

    const newSet = new Set(selected.map((q: Question) => q.id));
    setQuestionsToAdd(newSet);
    toast.success(`${count} random questions selected`);
    setShowSmartSelection(false);
  };

  const selectByDifficulty = (difficulty: 'easy' | 'medium' | 'hard') => {
    const available = allQuestions.filter(
      (q: Question) => q.difficulty === difficulty && !existingQuestionIds.has(q.id)
    );

    if (available.length === 0) {
      toast.error(`No ${difficulty} questions available`);
      return;
    }

    const newSet = new Set(available.map((q: Question) => q.id));
    setQuestionsToAdd(newSet);
    toast.success(`${available.length} ${difficulty} questions selected`);
  };

  const selectCustomMix = () => {
    const { easy, medium, hard } = customMix;
    const total = easy + medium + hard;

    if (total === 0) {
      toast.error('Please specify at least one question count');
      return;
    }

    const newSet = new Set<string>();

    // Get easy questions
    if (easy > 0) {
      const easyQuestions = allQuestions
        .filter((q: Question) => q.difficulty === 'easy' && !existingQuestionIds.has(q.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, easy);
      easyQuestions.forEach((q) => newSet.add(q.id));
    }

    // Get medium questions
    if (medium > 0) {
      const mediumQuestions = allQuestions
        .filter((q: Question) => q.difficulty === 'medium' && !existingQuestionIds.has(q.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, medium);
      mediumQuestions.forEach((q) => newSet.add(q.id));
    }

    // Get hard questions
    if (hard > 0) {
      const hardQuestions = allQuestions
        .filter((q: Question) => q.difficulty === 'hard' && !existingQuestionIds.has(q.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, hard);
      hardQuestions.forEach((q) => newSet.add(q.id));
    }

    setQuestionsToAdd(newSet);
    toast.success(`${newSet.size} questions selected (${easy} easy, ${medium} medium, ${hard} hard)`);
    setShowSmartSelection(false);
  };

  const handleAddQuestions = async () => {
    if (questionsToAdd.size === 0) {
      toast.error('Please select at least one question');
      return;
    }

    try {
      // Use allQuestions instead of just current page questions
      const selectedQuestions = allQuestions.filter((q: Question) =>
        questionsToAdd.has(q.id)
      );

      // Navigate back with the selected questions
      navigate(`/teacher/exams/${examId}/edit`, {
        state: { questionsToAdd: selectedQuestions },
      });

      toast.success(`${questionsToAdd.size} question(s) selected`);
    } catch {
      toast.error('Failed to add questions');
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const selectedBank = questionBanks.find((b: QuestionBank) => b.id === selectedBankId);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(`/teacher/exams/${examId}/edit`)}
          >
            Back to Exam
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Add Questions from Question Bank</h1>
            <p className="text-sm text-white/50 mt-1">
              Select questions to add to your exam
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <p className="text-sm text-white/50">Selected Questions</p>
            <p className="text-2xl font-bold text-teal-400">{questionsToAdd.size}</p>
          </div>
          <Button
            onClick={handleAddQuestions}
            disabled={questionsToAdd.size === 0}
            size="lg"
          >
            Add {questionsToAdd.size} Question{questionsToAdd.size !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>

      {/* Question Bank Selection */}
      <Card>
        <div className="p-6 space-y-4">
          <Select
            label="Select Question Bank"
            options={[
              { value: '', label: 'Choose a question bank...' },
              ...questionBanks.map((bank: QuestionBank) => ({
                value: bank.id,
                label: `${bank.name} (${bank.question_count || 0} questions) - ${bank.subject}`,
              })),
            ]}
            value={selectedBankId}
            onChange={(e) => handleBankChange(e.target.value)}
          />

          {selectedBank && (
            <>
              <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{selectedBank.name}</h3>
                  <p className="text-sm text-white/50">{selectedBank.description || 'No description'}</p>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <div>
                    <p className="text-white/50">Subject</p>
                    <p className="font-medium text-white">{selectedBank.subject}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Total Questions</p>
                    <p className="font-medium text-teal-400">{selectedBank.question_count || 0}</p>
                  </div>
                  {selectedBank.grade_level && (
                    <div>
                      <p className="text-white/50">Grade Level</p>
                      <p className="font-medium text-white">{selectedBank.grade_level}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary Stats - shows after questions are loaded */}
              {!isLoadingAllQuestions && allQuestions.length > 0 && (
                <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{allQuestions.length}</p>
                      <p className="text-xs text-white/50">Total in Bank</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-400">
                        {allQuestions.filter((q: Question) => existingQuestionIds.has(q.id)).length}
                      </p>
                      <p className="text-xs text-white/50">Already Added</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-teal-400">
                        {allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length}
                      </p>
                      <p className="text-xs text-white/50">Available</p>
                    </div>
                    <div className="text-center border-l border-white/10 pl-4">
                      <div className="flex justify-center gap-2">
                        <span className="text-green-400">{allQuestions.filter((q: Question) => q.difficulty === 'easy' && !existingQuestionIds.has(q.id)).length}E</span>
                        <span className="text-yellow-400">{allQuestions.filter((q: Question) => q.difficulty === 'medium' && !existingQuestionIds.has(q.id)).length}M</span>
                        <span className="text-red-400">{allQuestions.filter((q: Question) => q.difficulty === 'hard' && !existingQuestionIds.has(q.id)).length}H</span>
                      </div>
                      <p className="text-xs text-white/50">Available by Difficulty</p>
                    </div>
                    <div className="text-center border-l border-white/10 pl-4">
                      <div className="flex justify-center gap-2">
                        <span className="text-green-400/50">{allQuestions.filter((q: Question) => q.difficulty === 'easy' && existingQuestionIds.has(q.id)).length}E</span>
                        <span className="text-yellow-400/50">{allQuestions.filter((q: Question) => q.difficulty === 'medium' && existingQuestionIds.has(q.id)).length}M</span>
                        <span className="text-red-400/50">{allQuestions.filter((q: Question) => q.difficulty === 'hard' && existingQuestionIds.has(q.id)).length}H</span>
                      </div>
                      <p className="text-xs text-white/50">Already Added</p>
                    </div>
                  </div>

                  {/* All questions added message */}
                  {allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length === 0 && (
                    <div className="mt-4 flex items-center justify-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">
                        All {allQuestions.length} questions from this bank have been added to the exam
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Search and Filters */}
      {selectedBankId && (
        <>
          {/* Smart Selection */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Quick Selection Tools</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSmartSelection(!showSmartSelection)}
                  leftIcon={showSmartSelection ? <ChevronLeft className="w-4 h-4" /> : <Sliders className="w-4 h-4" />}
                  disabled={isLoadingAllQuestions}
                >
                  {showSmartSelection ? 'Hide' : 'Show'} Advanced Options
                </Button>
              </div>

              {isLoadingAllQuestions ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto mb-2"></div>
                  <p className="text-sm text-white/50">Loading all questions for selection...</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllInBank}
                  leftIcon={<Zap className="w-4 h-4" />}
                >
                  Select All ({allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectByDifficulty('easy')}
                  leftIcon={<Badge variant="success" size="sm">E</Badge>}
                >
                  All Easy ({allQuestions.filter((q: Question) => q.difficulty === 'easy' && !existingQuestionIds.has(q.id)).length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectByDifficulty('medium')}
                  leftIcon={<Badge variant="warning" size="sm">M</Badge>}
                >
                  All Medium ({allQuestions.filter((q: Question) => q.difficulty === 'medium' && !existingQuestionIds.has(q.id)).length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectByDifficulty('hard')}
                  leftIcon={<Badge variant="danger" size="sm">H</Badge>}
                >
                  All Hard ({allQuestions.filter((q: Question) => q.difficulty === 'hard' && !existingQuestionIds.has(q.id)).length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuestionsToAdd(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
              )}

              {!isLoadingAllQuestions && showSmartSelection && (
                <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-4">
                  {/* Random Selection */}
                  <div className="p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                    <div className="flex items-center space-x-3 mb-3">
                      <Shuffle className="w-5 h-5 text-teal-400" />
                      <h4 className="font-medium text-white">Random Selection</h4>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Input
                        type="number"
                        min="1"
                        max={allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length}
                        value={randomCount}
                        onChange={(e) => setRandomCount(parseInt(e.target.value) || 0)}
                        placeholder="Number of questions"
                        className="w-32"
                      />
                      <Button onClick={selectRandom} size="sm">
                        Select Random
                      </Button>
                      <span className="text-sm text-white/50">
                        Max: {allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length} available
                      </span>
                    </div>
                  </div>

                  {/* Custom Mix */}
                  <div className="p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                    <div className="flex items-center space-x-3 mb-3">
                      <Sliders className="w-5 h-5 text-teal-400" />
                      <h4 className="font-medium text-white">Custom Mix by Difficulty</h4>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-sm text-white/50 mb-1">Easy</label>
                        <Input
                          type="number"
                          min="0"
                          max={allQuestions.filter((q: Question) => q.difficulty === 'easy' && !existingQuestionIds.has(q.id)).length}
                          value={customMix.easy}
                          onChange={(e) => setCustomMix({ ...customMix, easy: parseInt(e.target.value) || 0 })}
                          placeholder="0"
                        />
                        <p className="text-xs text-white/30 mt-1">
                          Max: {allQuestions.filter((q: Question) => q.difficulty === 'easy' && !existingQuestionIds.has(q.id)).length}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-white/50 mb-1">Medium</label>
                        <Input
                          type="number"
                          min="0"
                          max={allQuestions.filter((q: Question) => q.difficulty === 'medium' && !existingQuestionIds.has(q.id)).length}
                          value={customMix.medium}
                          onChange={(e) => setCustomMix({ ...customMix, medium: parseInt(e.target.value) || 0 })}
                          placeholder="0"
                        />
                        <p className="text-xs text-white/30 mt-1">
                          Max: {allQuestions.filter((q: Question) => q.difficulty === 'medium' && !existingQuestionIds.has(q.id)).length}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-white/50 mb-1">Hard</label>
                        <Input
                          type="number"
                          min="0"
                          max={allQuestions.filter((q: Question) => q.difficulty === 'hard' && !existingQuestionIds.has(q.id)).length}
                          value={customMix.hard}
                          onChange={(e) => setCustomMix({ ...customMix, hard: parseInt(e.target.value) || 0 })}
                          placeholder="0"
                        />
                        <p className="text-xs text-white/30 mt-1">
                          Max: {allQuestions.filter((q: Question) => q.difficulty === 'hard' && !existingQuestionIds.has(q.id)).length}
                        </p>
                      </div>
                      <div className="flex items-end">
                        <Button onClick={selectCustomMix} className="w-full">
                          Apply Mix ({customMix.easy + customMix.medium + customMix.hard})
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftIcon={<Search className="w-4 h-4" />}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllOnPage}
                    disabled={isLoadingQuestions}
                  >
                    Select All on Page
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAllOnPage}
                    disabled={isLoadingQuestions}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="mt-3 text-sm text-white/50">
                Showing {questions.length} of {totalQuestions} questions
                {questionsToAdd.size > 0 && ` • ${questionsToAdd.size} selected`}
              </div>
            </div>
          </Card>

          {/* Questions List */}
          <Card>
            {isLoadingQuestions ? (
              <div className="text-center py-12">
                <div className="spinner mx-auto mb-2"></div>
                <p className="text-sm text-white/50">Loading questions...</p>
              </div>
            ) : questions.length > 0 ? (
              <>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader className="w-12">
                        <input
                          type="checkbox"
                          checked={
                            questions.filter((q: Question) => !existingQuestionIds.has(q.id)).length > 0 &&
                            questions
                              .filter((q: Question) => !existingQuestionIds.has(q.id))
                              .every((q: Question) => questionsToAdd.has(q.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllOnPage();
                            } else {
                              deselectAllOnPage();
                            }
                          }}
                          className="h-4 w-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                        />
                      </TableHeader>
                      <TableHeader>Question</TableHeader>
                      <TableHeader className="w-32">Type</TableHeader>
                      <TableHeader className="w-24">Difficulty</TableHeader>
                      <TableHeader className="w-20">Points</TableHeader>
                      <TableHeader className="w-28">Status</TableHeader>
                    </tr>
                  </thead>
                  <TableBody>
                    {questions.map((question: Question) => {
                      const isAlreadyAdded = existingQuestionIds.has(question.id);
                      const isSelected = questionsToAdd.has(question.id);

                      return (
                        <TableRow
                          key={question.id}
                          className={`cursor-pointer transition-colors ${
                            isAlreadyAdded
                              ? 'opacity-50 cursor-not-allowed'
                              : isSelected
                              ? 'bg-teal-500/10'
                              : 'hover:bg-white/5'
                          }`}
                          onClick={() => !isAlreadyAdded && toggleQuestionSelection(question.id)}
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isAlreadyAdded}
                              onChange={() => toggleQuestionSelection(question.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500 disabled:opacity-50"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="max-w-2xl">
                              <p className="text-sm text-white line-clamp-2">
                                {question.question_text}
                              </p>
                              {question.tags && question.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {question.tags.slice(0, 3).map((tag, idx) => (
                                    <Badge key={idx} variant="secondary" size="sm">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {question.tags.length > 3 && (
                                    <Badge variant="secondary" size="sm">
                                      +{question.tags.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="primary" size="sm">
                              {question.question_type?.replace('_', ' ') || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                question.difficulty === 'easy'
                                  ? 'success'
                                  : question.difficulty === 'medium'
                                  ? 'warning'
                                  : 'danger'
                              }
                              size="sm"
                            >
                              {question.difficulty || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-medium text-white">
                              {question.points}
                            </span>
                          </TableCell>
                          <TableCell>
                            {isAlreadyAdded ? (
                              <Badge variant="success" size="sm">
                                ✓ Added
                              </Badge>
                            ) : isSelected ? (
                              <Badge variant="info" size="sm">
                                Selected
                              </Badge>
                            ) : (
                              <Badge variant="secondary" size="sm">
                                Available
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-6 border-t border-white/[0.06]">
                    <div className="text-sm text-white/50">
                      Page {currentPage} of {totalPages} • {totalQuestions} total questions
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? 'primary' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight className="w-4 h-4" />}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                {/* Check if bank has questions but all are added vs bank is empty */}
                {allQuestions.length > 0 && allQuestions.filter((q: Question) => !existingQuestionIds.has(q.id)).length === 0 ? (
                  <>
                    <CheckCircle className="w-16 h-16 text-green-400/50 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-green-400 mb-2">All Questions Added</h3>
                    <p className="text-sm text-white/50">
                      All {allQuestions.length} questions from this bank have already been added to the exam.
                    </p>
                    <p className="text-sm text-white/30 mt-2">
                      Select a different question bank to add more questions.
                    </p>
                  </>
                ) : questions.length === 0 && allQuestions.length > 0 ? (
                  <>
                    <AlertCircle className="w-16 h-16 text-yellow-400/50 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-yellow-400 mb-2">No Matching Questions</h3>
                    <p className="text-sm text-white/50">
                      {searchQuery
                        ? `No questions match "${searchQuery}". Try adjusting your search.`
                        : 'No questions available on this page.'}
                    </p>
                  </>
                ) : (
                  <>
                    <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Questions in Bank</h3>
                    <p className="text-sm text-white/50">
                      This question bank doesn't have any questions yet.
                    </p>
                  </>
                )}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Empty State */}
      {!selectedBankId && (
        <Card>
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Select a Question Bank</h3>
            <p className="text-sm text-white/50">
              Choose a question bank from the dropdown above to browse and add questions
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};
